/**
 * Phase 2L-A16 — Admin Analytics Dashboard.
 *
 * Full business + operations overview. Admin-only (route gate sits in
 * App.js via `requireAdmin`). Data lives in `/api/admin/dashboard/analytics`
 * — see `dashboard_service.py` for computation rules.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck2, DollarSign, CheckCircle2, AlertTriangle, FlaskConical,
  Eye, FileCheck2, FilePlus2, MailWarning, Users2,
  TrendingUp, Map, Globe, Activity, Handshake,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { PortalShell } from "@/components/PortalShell";
import { PendingPmApplicationsBanner } from "@/components/PendingPmApplicationsBanner";
import api from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import {
  DashboardKpi, DashboardSection, EmptyState, LoadingSkeleton,
  FunnelBar, MiniBarRow, AlertRow, ActivityFeed, formatCents,
} from "@/components/dashboard/Primitives";
import { ShowTestDataToggle, useShowDemoToggle } from "@/components/ShowTestDataToggle";
import HomeHealthSummary from "@/components/HomeHealthSummary";
import { ReviewFunnelTile } from "@/components/admin/ReviewFunnelTile";
import { CommunicationsHealthTile } from "@/components/admin/CommunicationsHealthTile";
import { EmailFailuresByCategoryTile } from "@/components/admin/EmailFailuresByCategoryTile";
import { ResendDailyUsageTile } from "@/components/admin/ResendDailyUsageTile";
import { LocalLabConversionTile } from "@/components/LocalLabConversionTile";
import BookingAvailabilityBanner from "@/components/admin/BookingAvailabilityBanner";
import { RegionAllocationReviewWidget } from "@/components/RegionAllocationReviewWidget";
import { OverdueInvoicesTile } from "@/components/admin/OverdueInvoicesTile";
import PostedKitAddressRepairTile from "@/components/PostedKitAddressRepairTile";

const RANGES = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
];

const PIE_COLORS = ["#06b6d4", "#0891b2", "#0e7490", "#155e75", "#164e63", "#475569"];

export default function AdminAnalyticsDashboard() {
  const [range, setRange] = useState("30d");
  const [showDemo, setShowDemo] = useShowDemoToggle("admin.dashboard.showDemo");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Feb 2026 A145-fu-A · Step 1 — PWS Franchisee applications KPI.
  // Independent fetch so a slow / failed analytics call doesn't hide
  // the tile, and vice-versa.
  const [pwsAppsSummary, setPwsAppsSummary] = useState(null);

  useEffect(() => {
    document.title = "Aware Admin Dashboard";
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/admin/dashboard/analytics?range=${range}&show_demo=${showDemo ? "true" : "false"}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to load dashboard"); })
      .finally(() => { if (alive) setLoading(false); });
    api.get("/admin/pws-franchisee-interest/summary")
      .then(({ data }) => { if (alive) setPwsAppsSummary(data); })
      .catch(() => { /* Silent — the KPI tile just renders zeros. */ });
    return () => { alive = false; };
  }, [range, showDemo]);

  const k = data?.kpis;
  const b = data?.breakdowns;
  const a = data?.alerts;

  return (
    <PortalShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Admin</div>
            <h1 data-testid="admin-analytics-heading" className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2">Aware Admin Dashboard</h1>
            <p className="text-sm text-slate-500 mt-2">Business + operations snapshot across bookings, revenue, lab workflow, and customer insights.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/analytics" data-testid="admin-dashboard-monthly-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 px-3.5 py-1.5 text-sm font-semibold text-cyan-800 hover:bg-cyan-100 transition">
              Monthly Performance →
            </Link>
            <ShowTestDataToggle
              showDemo={showDemo}
              onChange={setShowDemo}
              testId="admin-dashboard-show-demo-toggle"
            />
            <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="admin-dashboard-range-selector">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  data-testid={`admin-dashboard-range-${r.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${range === r.key ? "bg-cyan-600 text-white" : "text-slate-600 hover:text-slate-900"}`}
                >{r.label}</button>
              ))}
            </div>
          </div>
        </div>

        {showDemo && (
          <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-800 font-semibold inline-flex items-center gap-2" data-testid="admin-dashboard-demo-banner">
            <AlertTriangle className="w-3.5 h-3.5" />
            Showing demo / test / fixture data. Switch off to return to real customer view.
          </div>
        )}

        {/* Phase 2L-A132 — pending PM applications banner. Hidden when
            count is 0 so the dashboard stays calm. */}
        <PendingPmApplicationsBanner />

        {/* Phase 2L-A40 — Today's Attention summary card sits above the
            analytics strip. Calm, role-aware, single source of "what
            needs doing right now". The KPI strip below stays as the
            broader business analytics view. */}
        <div className="mb-6">
          <HomeHealthSummary />
        </div>

        {/* Feb 2026 — Booking Availability banner (silent unless a rule
            is active or upcoming within 30 days). */}
        <BookingAvailabilityBanner />

        {/* Aware Partner Network Phase 5A — region-allocation review queue.
            Surfaces ambiguous Phase 4 matches that the resolver SAFELY
            parked instead of randomly assigning. Read-only admin
            visibility; never assigns partners, never sends email. */}
        <RegionAllocationReviewWidget />

        {loading ? <LoadingSkeleton /> : error ? (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700">{error}</div>
        ) : !data ? <EmptyState /> : (
          <>
            {/* KPI strip — 10 tiles */}
            <div data-testid="admin-dashboard-kpis" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              <DashboardKpi label="Bookings" value={k.bookings_period} hint={`in last ${data.range_days}d`} icon={CalendarCheck2} to="/admin/bookings" testId="kpi-bookings" />
              <DashboardKpi label="Revenue" value={formatCents(k.revenue_period_cents)} hint="paid only" icon={DollarSign} testId="kpi-revenue" tone="success" />
              <DashboardKpi label="Paid bookings" value={k.paid_bookings} icon={CheckCircle2} testId="kpi-paid" />
              <DashboardKpi label="Awaiting payment" value={k.awaiting_payment} icon={AlertTriangle} tone={k.awaiting_payment > 0 ? "warn" : "default"} testId="kpi-awaiting" to="/admin/bookings?filter=awaiting" />
              <DashboardKpi label="Pending drop-off" value={k.samples_pending_dropoff} icon={FlaskConical} testId="kpi-pending-dropoff" to="/admin/lab" />
              <DashboardKpi label="Awaiting review" value={k.reports_awaiting_review} icon={Eye} tone={k.reports_awaiting_review > 0 ? "warn" : "default"} testId="kpi-awaiting-review" to="/admin/lab?status=awaiting_review" />
              <DashboardKpi label="Reports published" value={k.reports_published_period} icon={FileCheck2} testId="kpi-published" />
              {/* Phase 2L-A94 — defensive read so the tile is never blank.
                  Backend shape: {created, requested, total} since A60.
                  We coerce to numbers + recompute `total` so a missing/
                  partial payload still renders a safe `0` rather than
                  rendering an empty value (React shows nothing on
                  undefined). */}
              {(() => {
                const raw = k.pws_estimates;
                const isObj = raw && typeof raw === "object";
                const created = Number((isObj ? raw.created : 0) ?? 0) || 0;
                const requested = Number((isObj ? raw.requested : 0) ?? 0) || 0;
                const total = isObj && raw.total != null
                  ? (Number(raw.total) || 0)
                  : (typeof raw === "number" ? raw : created + requested);
                return (
                  <DashboardKpi
                    label="PWS estimates"
                    value={total}
                    hint={`${created} created · ${requested} requested`}
                    icon={FilePlus2}
                    testId="kpi-pws"
                    to="/admin/proposals"
                  />
                );
              })()}
              <DashboardKpi label="Open follow-ups" value={k.open_followups} icon={MailWarning} testId="kpi-followups" to="/admin/customer-requests" />
              <DashboardKpi label="Guest customers" value={k.guest_customers_total} icon={Users2} testId="kpi-guest-customers" to="/admin/customers" />
              {/* Feb 2026 A145-fu-A · Step 1 — PWS Franchisee applications tile.
                  Reads from a separate `/admin/pws-franchisee-interest/summary`
                  endpoint so the tile stays independent of the main
                  analytics payload. Tone = warn when there are unactioned
                  `new` leads so the badge is visible at a glance. */}
              <DashboardKpi
                label="PWS applications"
                value={pwsAppsSummary?.by_status?.new ?? 0}
                hint={pwsAppsSummary
                  ? `${pwsAppsSummary.converted ?? 0} converted · ${pwsAppsSummary.total ?? 0} total`
                  : "loading…"}
                icon={Handshake}
                tone={(pwsAppsSummary?.by_status?.new ?? 0) > 0 ? "warn" : "default"}
                testId="kpi-pws-applications"
                to="/admin/pws-franchisee-interest?status=new"
              />
            </div>

            {/* Trends + Breakdowns */}
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
              <OverdueInvoicesTile />
              <PostedKitAddressRepairTile />
            </div>
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
              <DashboardSection title="Bookings trend" subtitle={`Daily, last ${data.range_days} days`} testId="chart-bookings-trend">
                <TrendChart rows={data.trends.bookings_daily} dataKey="count" stroke="#0891b2" />
              </DashboardSection>
              <DashboardSection title="Revenue trend" subtitle="Paid only" testId="chart-revenue-trend">
                <TrendChart rows={data.trends.revenue_daily} dataKey="cents" stroke="#059669" formatY={(v) => `$${(v/100).toFixed(0)}`} />
              </DashboardSection>
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-6">
              <DashboardSection title="Test packages" subtitle="Booking mix" testId="chart-test-packages">
                <DonutChart rows={b.test_packages} labelKey="test_id" />
              </DashboardSection>
              <DashboardSection title="Payment status" subtitle="Period bookings" testId="chart-payment-status">
                <DonutChart rows={b.payment_status} labelKey="status" />
              </DashboardSection>
              <DashboardSection title="Lab workflow funnel" subtitle="All time, current state" testId="chart-lab-funnel">
                <FunnelBar stages={b.lab_funnel} totalLabel="tests across stages" />
              </DashboardSection>
            </div>

            <div className="grid lg:grid-cols-2 gap-4 mb-6">
              <DashboardSection title="Treatment opportunities" subtitle="By recommendation category" testId="chart-recommendations">
                <MiniBarRow rows={b.recommendations} labelKey="category" valueKey="count" />
              </DashboardSection>
              <DashboardSection title="Top suburbs" subtitle="By booking count" testId="chart-top-suburbs" action={<Map className="w-4 h-4 text-slate-400" />}>
                <MiniBarRow rows={b.top_suburbs} labelKey="suburb" valueKey="count" />
              </DashboardSection>
            </div>

            {/* Review Follow-up funnel — admin-only revenue/marketing
                pipeline visibility. Read-only; never mutates. */}
            <div className="mb-6">
              <ReviewFunnelTile />
            </div>

            {/* Feb 2026 — All-Inclusive Pricing pilot visibility.
                Mail-out vs lab-attendance split by postcode over the
                last 30 days. Powered by
                /api/admin/analytics/local-lab-conversion. */}
            <div className="mb-6">
              <LocalLabConversionTile />
            </div>

            {/* Communications health — A88. Read-only operational pulse
                across email/inbound/referral pipes. Admin-only. */}
            <div className="mb-6">
              <CommunicationsHealthTile />
            </div>

            {/* Email failures · by category — A136. Read-only operator
                visibility for outbound email failures, grouped by
                operator-friendly bucket. Three windows (24h/7d/30d). */}
            <div className="mb-6">
              <EmailFailuresByCategoryTile />
            </div>

            {/* Resend Daily Usage — A138. 14-day daily volume + estimated
                spend trajectory. Read-only. Pairs with the A136/A137
                reliability work to round out the email-health story. */}
            <div className="mb-6">
              <ResendDailyUsageTile />
            </div>

            {/* Operational alerts */}
            <DashboardSection title="Operational alerts" subtitle="Only alerts with active issues are shown" testId="admin-dashboard-alerts">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <AlertRow title="Paid bookings missing is_paid" {...a.paid_missing_is_paid} hint="Run the payment backfill if non-zero" tone="warn" />
                <AlertRow title="Awaiting payment > 24h" {...a.awaiting_payment_over_24h} tone="warn" to="/admin/bookings?filter=awaiting" />
                <AlertRow title="Samples pending > 7 days" {...a.samples_pending_over_7d} tone="warn" to="/admin/lab?status=pending_sample" />
                <AlertRow title="Reports awaiting review" {...a.reports_awaiting_review} tone="warn" to="/admin/lab?status=awaiting_review" />
                <AlertRow title="Completed, not published" {...a.completed_not_published} tone="warn" to="/admin/lab?status=approved" />
                <AlertRow title="Failed emails (24h)" count={a.failed_emails_24h.count} items={a.failed_emails_24h.items} tone="warn" to="/admin/emails" />
                <AlertRow title="Missing customer phone" count={a.missing_customer_phone.count} items={a.missing_customer_phone.customers} tone="warn" to="/admin/customers" />
                <AlertRow title="Critical microbiological detections" {...a.critical_micro_findings} tone="danger" to="/admin/lab" />
                <AlertRow title="PWS opportunities not actioned" count={a.pws_opportunities_open.count} items={a.pws_opportunities_open.items} tone="warn" to="/admin/proposals" />
              </div>
              {Object.values(a).every((x) => !x.count) && <div className="text-sm text-emerald-600 italic">All clear — no operational alerts.</div>}
            </DashboardSection>

            {/* SEO + recent activity */}
            <div className="grid lg:grid-cols-2 gap-4 mt-6">
              <DashboardSection title="Local SEO publishing" subtitle="Published vs draft suburb pages" testId="chart-seo" action={<Globe className="w-4 h-4 text-slate-400" />}>
                <SeoPanel seo={b.seo_pages} />
              </DashboardSection>
              <DashboardSection title="Recent activity" subtitle="Latest 10 events" testId="recent-activity" action={<Activity className="w-4 h-4 text-slate-400" />}>
                <ActivityFeed events={data.recent_activity} />
              </DashboardSection>
            </div>

            <div className="text-[11px] text-slate-400 mt-6 text-right">Generated: {formatDateTimeAU(data.generated_at)}</div>
          </>
        )}
      </div>
    </PortalShell>
  );
}

function TrendChart({ rows, dataKey, stroke, formatY }) {
  if (!rows?.length) return <EmptyState />;
  return (
    <div style={{ width: "100%", height: 180 }}>
      <ResponsiveContainer>
        <LineChart data={rows}>
          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={formatY} width={50} />
          <Tooltip formatter={(v) => formatY ? formatY(v) : v} />
          <Line type="monotone" dataKey={dataKey} stroke={stroke} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function DonutChart({ rows, labelKey }) {
  if (!rows?.length) return <EmptyState />;
  const data = rows.map((r) => ({ name: r[labelKey] || "unknown", value: r.count }));
  return (
    <div style={{ width: "100%", height: 200 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={42} outerRadius={70}>
            {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
          </Pie>
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function SeoPanel({ seo }) {
  const total = seo?.total || 0;
  const pct = total ? Math.round((seo.published / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-2xl font-display font-bold text-slate-900">{seo?.published || 0}<span className="text-slate-400 text-base font-normal"> / {total}</span></div>
          <div className="text-xs text-slate-500">{pct}% of suburb pages published</div>
        </div>
        <Link to="/locations" target="_blank" rel="noopener" className="text-xs font-semibold text-cyan-700 hover:underline" data-testid="seo-suburb-list-link">Suburb list →</Link>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-cyan-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs">
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-cyan-500" /> Published <span className="font-semibold text-slate-900">{seo?.published || 0}</span></div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-300" /> Draft <span className="font-semibold text-slate-900">{seo?.draft || 0}</span></div>
      </div>
    </div>
  );
}
